import React, { useEffect, useRef } from "react";

const LIT_HEIGHTS = [5, 9, 14, 8, 3];
const FLAT_HEIGHT = 3;
const LIT_COLOR = "#2ACBD1";
const FLAT_COLOR = "rgba(255,255,255,0.3)";
const FAST_MS = 100;
const SLOW_MS = 500;

// Maps RMS of time-domain samples (0..1) to a count of lit bars (0..5).
const rmsToLevel = (rms: number): number => (rms < 0.01 ? 0 : Math.min(5, 1 + Math.floor(rms * 25)));

export const MicLevelBars = ({ stream, className }: { stream: MediaStream | null; className?: string }) => {
    const barRefs = useRef<(HTMLElement | null)[]>([]);

    useEffect(() => {
        const paint = (level: number) => {
            barRefs.current.forEach((bar, i) => {
                if (!bar) return;
                const lit = i < level;
                bar.style.height = `${lit ? LIT_HEIGHTS[i] : FLAT_HEIGHT}px`;
                bar.style.background = lit ? LIT_COLOR : FLAT_COLOR;
            });
        };
        paint(0);

        const Ctor: typeof AudioContext | undefined =
            typeof window !== "undefined" ? window.AudioContext || (window as any).webkitAudioContext : undefined;
        if (!stream || !Ctor) return;

        let audioContext: AudioContext;
        let analyser: AnalyserNode;
        try {
            audioContext = new Ctor();
            analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            audioContext.createMediaStreamSource(stream).connect(analyser);
            // An AudioContext created outside a user gesture can start suspended (WebKit does this), and a suspended analyser reads silence.
            if (audioContext.state === "suspended") void audioContext.resume().catch(() => undefined);
        } catch {
            return;
        }

        const reduced =
            typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const interval = reduced ? SLOW_MS : FAST_MS;
        const samples = new Uint8Array(analyser.fftSize);
        let frame = 0;
        let last = 0;

        const tick = (now: number) => {
            frame = requestAnimationFrame(tick);
            if (now - last < interval) return;
            last = now;
            analyser.getByteTimeDomainData(samples);
            let sum = 0;
            for (let i = 0; i < samples.length; i++) {
                const v = (samples[i] - 128) / 128;
                sum += v * v;
            }
            paint(rmsToLevel(Math.sqrt(sum / samples.length)));
        };
        frame = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(frame);
            audioContext.close().catch(() => undefined);
        };
    }, [stream]);

    return (
        <span aria-hidden="true" className={`inline-flex h-[14px] flex-none items-end gap-[2px] ${className ?? ""}`}>
            {LIT_HEIGHTS.map((_, i) => (
                <i
                    key={i}
                    ref={(el) => {
                        barRefs.current[i] = el;
                    }}
                    className="block w-[3px] rounded-[2px]"
                    style={{ height: FLAT_HEIGHT, background: FLAT_COLOR }}
                />
            ))}
        </span>
    );
};
